/**
 * The parts of the public contact form that are rules rather than rendering, kept apart so
 * they can be tested without a browser.
 */

/** What the page tells the API about how the person arrived. Mirrors `LeadTracking`. */
export interface LeadTracking {
  utmSource: string | null
  utmMedium: string | null
  utmCampaign: string | null
  utmTerm: string | null
  utmContent: string | null
  gclid: string | null
  fbclid: string | null
  landingPageUrl: string | null
  referrer: string | null
}

/** Where the form is being shown, and how the person got there. */
export interface Arrival {
  /** True inside an iframe on somebody else's page. */
  embedded: boolean
  /** The page the person actually landed on - the host page when embedded. */
  page: string
  referrer: string | null
  /** The host page's site, reported so the API can check it against the form's list. */
  embeddedOn: string | null
}

/**
 * Works out where the person is from the form page's own address.
 *
 * <p>
 * Embedded, the form page's address is ours and says nothing about the campaign: `embed.js`
 * passes the host page's address and referrer in `page` and `ref`. `ancestorOrigins` is
 * preferred over that for the host, where the browser offers it, because the browser fills
 * it in and a page cannot.
 * </p>
 */
export function arrivalFrom(
  href: string,
  documentReferrer: string,
  ancestorOrigins: readonly string[] | undefined,
): Arrival {
  const own = new URL(href)
  const embedded = own.searchParams.get('embed') === '1'

  if (!embedded) {
    return { embedded, page: href, referrer: documentReferrer || null, embeddedOn: null }
  }

  const page = own.searchParams.get('page') || documentReferrer || href
  const referrer = own.searchParams.get('ref') || null
  const embeddedOn = ancestorOrigins?.[0] ?? originOf(page) ?? originOf(documentReferrer)

  return { embedded, page, referrer, embeddedOn }
}

/** The campaign tags and click ids on the page the person landed on. */
export function trackingFrom(arrival: Arrival): LeadTracking {
  let params: URLSearchParams

  try {
    params = new URL(arrival.page).searchParams
  } catch {
    params = new URLSearchParams()
  }

  const read = (name: string) => {
    const value = params.get(name)?.trim()
    return value ? value : null
  }

  return {
    utmSource: read('utm_source'),
    utmMedium: read('utm_medium'),
    utmCampaign: read('utm_campaign'),
    utmTerm: read('utm_term'),
    utmContent: read('utm_content'),
    gclid: read('gclid'),
    fbclid: read('fbclid'),
    landingPageUrl: arrival.page,
    referrer: arrival.referrer,
  }
}

function originOf(url: string | null | undefined): string | null {
  if (!url) return null

  try {
    return new URL(url).origin
  } catch {
    return null
  }
}

/** The messages the form page sends the page it is embedded in. `embed.js` listens for them. */
export const EMBED_MESSAGE = {
  height: 'codemax-form:height',
  redirect: 'codemax-form:redirect',
} as const

/**
 * Pairs half-width questions into rows, so two half fields sit side by side and a half
 * field followed by a full one does not leave a hole beside it.
 */
export function rowsOf<T extends { width?: string | null }>(fields: readonly T[]): T[][] {
  const rows: T[][] = []
  let pending: T | null = null

  for (const field of fields) {
    if (field.width === 'half') {
      if (pending) {
        rows.push([pending, field])
        pending = null
      } else {
        pending = field
      }

      continue
    }

    if (pending) {
      rows.push([pending])
      pending = null
    }

    rows.push([field])
  }

  if (pending) rows.push([pending])

  return rows
}
